/**
 * Iter123 — Admin Edit Approvals.
 *
 * Cross-user edit queue: shows pending edit requests where someone tried
 * to change another user's record. Owner or admin can approve/reject.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { UserCheck, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';

const dt = (v) => v ? new Date(v).toLocaleString() : '—';
const STATUSES = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];

export default function AdminEditApprovals() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('PENDING');
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = status === 'ALL' ? `${BACKEND_URL}/api/edit-approvals` : `${BACKEND_URL}/api/edit-approvals?status=${status}`;
      const r = await fetch(url, { headers: H });
      setRows(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [status, token]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    const r = await fetch(`${BACKEND_URL}/api/edit-approvals/${id}/resolve`, {
      method: 'POST', headers: JH, body: JSON.stringify({ action, notes: notes[id] || '' }),
    });
    if (!r.ok) return toast({ title: 'Failed', variant: 'destructive' });
    toast({ title: `Request ${action.toLowerCase()}` });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-edit-approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-purple-300" />
          </div>
          <div>
            <div className="text-xs text-purple-300 uppercase tracking-wider font-mono">Admin · Approvals</div>
            <h1 className="text-2xl font-bold text-white">Cross-User Edit Requests</h1>
            <p className="text-slate-400 text-sm">Approve or reject edits proposed by another user against records they don't own.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-32" data-testid="approvals-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-slate-300"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>
      </div>

      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader><CardTitle className="text-sm text-white">Requests ({rows.length})</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">Nothing pending.</div> : (
            <div className="space-y-3">
              {rows.map(a => (
                <div key={a.id} className="border border-slate-700 bg-slate-900/60 rounded p-3" data-testid={`approval-row-${a.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-white text-sm">
                      <b>{a.resource_type}</b> #{a.resource_id}
                      <Badge className={`ml-2 text-[10px] ${a.status === 'PENDING' ? 'bg-amber-500/20 text-amber-300' : a.status === 'APPROVED' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{a.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-2">
                      <Clock className="w-3 h-3" /> {dt(a.requested_at)}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 mb-2">
                    Requested by <b className="text-slate-200">{a.requested_by_name || a.requested_by}</b> · Owner user: <span className="font-mono">{a.owner_user_id || '—'}</span>
                  </div>
                  {a.reason && <div className="text-xs text-slate-300 mb-2 italic">"{a.reason}"</div>}
                  <div className="text-[10px] text-slate-400 uppercase font-mono mb-1">Proposed changes</div>
                  <pre className="text-[11px] bg-slate-950 border border-slate-800 rounded p-2 text-slate-200 overflow-x-auto max-h-40">{JSON.stringify(a.patch, null, 2)}</pre>
                  {a.status === 'PENDING' && (
                    <div className="mt-2 space-y-2">
                      <Textarea value={notes[a.id] || ''} onChange={e => setNotes(prev => ({ ...prev, [a.id]: e.target.value }))}
                        placeholder="Resolution notes" rows={1} className="bg-slate-800 border-slate-700 text-white text-xs" data-testid={`approval-notes-${a.id}`} />
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => resolve(a.id, 'APPROVE')} data-testid={`approval-approve-${a.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve & Apply
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-500/40 !text-red-300" onClick={() => resolve(a.id, 'REJECT')} data-testid={`approval-reject-${a.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  )}
                  {a.resolve_notes && <div className="text-[10px] text-slate-500 mt-2 italic">Resolution: {a.resolve_notes}</div>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
